/**
 * MapPulse.js
 * Efeito de pulso estilo Waze ao redor do dispositivo selecionado
 * - 3 círculos concêntricos com animação defasada
 * - Cores adaptam ao tema claro/escuro
 * - Tamanho proporcional ao zoom do mapa
 */
import {
 useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { map } from '../core/MapView';
import './MapPulse.css';

// Cores do pulso por tema (estilo Waze)
const PULSE_COLORS = {
  light: {
    background: 'rgba(51, 181, 229, 0.15)',
    border: 'rgba(51, 181, 229, 0.4)',
  },
  dark: {
    background: 'rgba(0, 188, 212, 0.15)',
    border: 'rgba(0, 188, 212, 0.4)',
  },
};

// Tamanho do pulso baseado no zoom
const getPulseSize = (zoom) => {
  if (zoom < 12) return 40;
  if (zoom < 14) return 60;
  if (zoom < 16) return 80;
  return 100;
};

const MapPulse = ({ mapReady }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  // 2ECOMP: shallowEqual evita re-renders desnecessários
  const positions = useSelector((state) => state.session.positions, shallowEqual);

  // 2ECOMP: PremiumUI Refresh — não mostrar pulse em device oculto
  const hiddenDeviceIds = useSelector((state) => state.devices.hiddenDeviceIds);
  const hiddenSet = useMemo(() => new Set(hiddenDeviceIds), [hiddenDeviceIds]);

  const [screenPosition, setScreenPosition] = useState(null);
  const [pulseSize, setPulseSize] = useState(60);
  const portalContainerRef = useRef(null);

  // Encontrar o container do mapa para o portal
  useEffect(() => {
    if (mapReady) {
      const mapContainer = map.getContainer();
      if (mapContainer && !portalContainerRef.current) {
        portalContainerRef.current = mapContainer;
      }
    }
  }, [mapReady]);

  // Atualiza posição do pulso na tela
  const updatePulsePosition = useCallback(() => {
    if (!mapReady || !selectedDeviceId || !positions[selectedDeviceId]) {
      setScreenPosition(null);
      return;
    }

    const position = positions[selectedDeviceId];
    if (!position || position.latitude == null || position.longitude == null) {
      setScreenPosition(null);
      return;
    }

    try {
      // Converter LatLng para pixels na tela
      const pixel = map.project([position.longitude, position.latitude]);
      setScreenPosition({ x: pixel.x, y: pixel.y });

      // Atualizar tamanho baseado no zoom atual
      const currentZoom = map.getZoom();
      setPulseSize(getPulseSize(currentZoom));
    } catch {
      setScreenPosition(null);
    }
  }, [mapReady, selectedDeviceId, positions]);

  // Atualizar quando device selecionado ou posição mudar
  useEffect(() => {
    updatePulsePosition();
  }, [updatePulsePosition]);

  // Atualizar quando mapa move ou zoom muda
  useEffect(() => {
    if (!mapReady) return;

    const handleMapMove = () => updatePulsePosition();
    const handleZoom = () => updatePulsePosition();

    map.on('move', handleMapMove);
    map.on('zoom', handleZoom);

    return () => {
      map.off('move', handleMapMove);
      map.off('zoom', handleZoom);
    };
  }, [mapReady, updatePulsePosition]);

  // 2ECOMP: PremiumUI Refresh — não renderizar se device oculto, sem seleção, ou sem container
  if (!selectedDeviceId || !screenPosition || !portalContainerRef.current || hiddenSet.has(selectedDeviceId)) {
    return null;
  }

  const colorScheme = isDarkMode ? PULSE_COLORS.dark : PULSE_COLORS.light;
  const themeClass = isDarkMode ? 'dark' : 'light';

  const pulseElement = (
    <div
      className="map-pulse-container"
      style={{
        left: screenPosition.x,
        top: screenPosition.y,
        width: pulseSize,
        height: pulseSize,
      }}
    >
      {/* 3 círculos concêntricos com animação defasada */}
      <div
        className={`pulse-wave ${themeClass}`}
        style={{
          width: pulseSize,
          height: pulseSize,
          backgroundColor: colorScheme.background,
          borderColor: colorScheme.border,
        }}
      />
      <div
        className={`pulse-wave ${themeClass} delay-1`}
        style={{
          width: pulseSize,
          height: pulseSize,
          backgroundColor: colorScheme.background,
          borderColor: colorScheme.border,
        }}
      />
      <div
        className={`pulse-wave ${themeClass} delay-2`}
        style={{
          width: pulseSize,
          height: pulseSize,
          backgroundColor: colorScheme.background,
          borderColor: colorScheme.border,
        }}
      />
    </div>
  );

  return createPortal(pulseElement, portalContainerRef.current);
};

// Indica que este componente precisa esperar o mapa estar pronto
MapPulse.handlesMapReady = true;

export default MapPulse;
